import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Snowflake } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody, CardHeader } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { formatarDataHora } from '@/lib/format';
import { Modal } from '@/features/tickets/Modal';
import { useOpcoesDeFormulario } from '@/features/tickets/opcoes';
import { AvisoDeFalha } from '@/features/workflow/componentes';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import { deCampoLocal, paraCampoLocal } from '@/features/changes/calendario';
import {
  alterarCongelamento,
  chavesDeMudanca,
  criarCongelamento,
  excluirCongelamento,
  listarCongelamentos,
  listarServicos,
} from '@/features/changes/changes.api';
import {
  Campo,
  CLASSE_DE_CAMPO,
  EtiquetaDeEnforcement,
} from '@/features/changes/componentes';
import type { Congelamento, Enforcement } from '@/features/changes/tipos';

/**
 * Os períodos de congelamento.
 *
 * ## Por que existem dois graus
 *
 * `BLOCKING` recusa o agendamento e só cede a uma exceção aprovada. `ADVISORY`
 * avisa e deixa passar — e ele existe porque congelamento que não se pode furar
 * vira congelamento que a equipe contorna registrando a mudança **depois**, que
 * é exatamente o indicador de mudança não autorizada que o processo quer
 * eliminar.
 *
 * ## Escopo vazio é o tenant inteiro
 *
 * Congelamento de fim de ano não se enumera serviço a serviço, e exigir a
 * enumeração faria o caso comum ser o mais trabalhoso de configurar. A tela diz
 * isso na cara, porque um formulário com três listas vazias parece incompleto.
 */
export function CongelamentosPage() {
  const [criando, setCriando] = useState(false);
  const consulta = useQuery({
    queryKey: chavesDeMudanca.congelamentos,
    queryFn: listarCongelamentos,
  });

  const vigentes = (consulta.data ?? []).filter((c) => c.active);
  const desligados = (consulta.data ?? []).filter((c) => !c.active);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={() => setCriando(true)}
          icon={<Snowflake className="h-4 w-4" aria-hidden />}
        >
          Declarar congelamento
        </Button>
      </div>

      {consulta.isPending ? (
        <SkeletonRegion label="Carregando congelamentos" className="space-y-2">
          <Skeleton className="h-24 w-full" />
        </SkeletonRegion>
      ) : null}

      {consulta.isError ? (
        <AvisoDeFalha
          falha={classificarFalha(consulta.error)}
          aoRecarregar={() => void consulta.refetch()}
        />
      ) : null}

      {consulta.data?.length === 0 ? (
        <EmptyState
          icon={<Snowflake className="h-7 w-7" aria-hidden />}
          title="Nenhum congelamento declarado"
          description="Declarar um congelamento é permissão de aprovação, não de abertura: quem congela o ambiente no fechamento do ano não é quem abre mudança."
          action={<Button size="sm" onClick={() => setCriando(true)}>Declarar o primeiro</Button>}
        />
      ) : null}

      {vigentes.map((congelamento) => (
        <CartaoDeCongelamento key={congelamento.id} congelamento={congelamento} />
      ))}

      {desligados.length > 0 ? (
        <section className="space-y-3 pt-2">
          <h3 className="text-sm font-semibold text-muted-foreground">
            Desligados ({desligados.length})
          </h3>
          {desligados.map((congelamento) => (
            <CartaoDeCongelamento key={congelamento.id} congelamento={congelamento} />
          ))}
        </section>
      ) : null}

      {criando ? <DialogoDeCongelamento aoFechar={() => setCriando(false)} /> : null}
    </div>
  );
}

function CartaoDeCongelamento({ congelamento }: { congelamento: Congelamento }) {
  const cliente = useQueryClient();
  const [editando, setEditando] = useState(false);
  const [falha, setFalha] = useState<Falha | null>(null);

  const alternar = useMutation({
    mutationFn: () =>
      alterarCongelamento(congelamento.id, {
        active: !congelamento.active,
        version: congelamento.version,
      }),
    onMutate: () => setFalha(null),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesDeMudanca.raiz });
      void cliente.invalidateQueries({ queryKey: ['chamados'] });
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const excluir = useMutation({
    mutationFn: () => excluirCongelamento(congelamento.id),
    onMutate: () => setFalha(null),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chavesDeMudanca.congelamentos }),
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const semEscopo =
    congelamento.scopeServiceIds.length === 0 &&
    congelamento.scopeCiIds.length === 0 &&
    congelamento.scopeOrganizationIds.length === 0;

  return (
    <Card className={cn(!congelamento.active && 'opacity-60')}>
      <CardHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {congelamento.name}
            <EtiquetaDeEnforcement enforcement={congelamento.enforcement} />
            {congelamento.active ? null : (
              <span className="text-xs font-normal text-muted-foreground">desligado</span>
            )}
          </span>
        }
        description={
          <span className="tabular-nums">
            {formatarDataHora(congelamento.startsAt)} → {formatarDataHora(congelamento.endsAt)}
          </span>
        }
        action={
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setEditando(true)}>
              Editar
            </Button>
            <Button
              variant={congelamento.active ? 'danger' : 'secondary'}
              size="sm"
              loading={alternar.isPending}
              onClick={() => alternar.mutate()}
            >
              {congelamento.active ? 'Desligar' : 'Religar'}
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={excluir.isPending}
              onClick={() => excluir.mutate()}
            >
              Excluir
            </Button>
          </div>
        }
      />
      <CardBody className="space-y-2 text-sm">
        <p>{congelamento.reason}</p>
        <p className="text-xs text-muted-foreground">
          {semEscopo
            ? 'Escopo: o tenant inteiro. Vale para qualquer mudança agendada no período.'
            : `Escopo: ${congelamento.scopeServiceIds.length} serviço(s), ${congelamento.scopeCiIds.length} item(ns) de configuração, ${congelamento.scopeOrganizationIds.length} organização(ões). Basta uma interseção para o congelamento alcançar a mudança.`}
        </p>
        {falha ? <AvisoDeFalha falha={falha} /> : null}
      </CardBody>

      {editando ? (
        <DialogoDeCongelamento congelamento={congelamento} aoFechar={() => setEditando(false)} />
      ) : null}
    </Card>
  );
}

// ── formulário ─────────────────────────────────────────────────────────────

function DialogoDeCongelamento({
  congelamento,
  aoFechar,
}: {
  congelamento?: Congelamento;
  aoFechar: () => void;
}) {
  const cliente = useQueryClient();
  const [nome, setNome] = useState(congelamento?.name ?? '');
  const [motivo, setMotivo] = useState(congelamento?.reason ?? '');
  const [inicio, setInicio] = useState(paraCampoLocal(congelamento?.startsAt));
  const [fim, setFim] = useState(paraCampoLocal(congelamento?.endsAt));
  const [enforcement, setEnforcement] = useState<Enforcement>(
    congelamento?.enforcement ?? 'BLOCKING',
  );
  const [servicos, setServicos] = useState<string[]>(congelamento?.scopeServiceIds ?? []);
  const [organizacoes, setOrganizacoes] = useState<string[]>(
    congelamento?.scopeOrganizationIds ?? [],
  );
  const [falha, setFalha] = useState<Falha | null>(null);

  const catalogo = useQuery({
    queryKey: chavesDeMudanca.servicos,
    queryFn: listarServicos,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const opcoes = useOpcoesDeFormulario();

  const inicioIso = deCampoLocal(inicio);
  const fimIso = deCampoLocal(fim);
  const intervaloValido =
    inicioIso !== null && fimIso !== null && new Date(fimIso) > new Date(inicioIso);
  const incompleto = nome.trim().length < 3 || motivo.trim().length < 3 || !intervaloValido;

  const gravar = useMutation({
    mutationFn: async () => {
      if (inicioIso === null || fimIso === null) throw new Error('Datas inválidas.');
      const corpo = {
        name: nome.trim(),
        reason: motivo.trim(),
        startsAt: inicioIso,
        endsAt: fimIso,
        enforcement,
        scopeServiceIds: servicos,
        // O escopo de item de configuração existe no servidor e não é oferecido
        // aqui: não há rota que liste o CMDB, e um seletor que só enxerga os
        // ICs que já são componentes de algum serviço daria a impressão errada
        // de que aquilo é o inventário inteiro. Congelamento por serviço e por
        // organização cobre o que a operação declara hoje.
        scopeOrganizationIds: organizacoes,
      };
      return congelamento
        ? alterarCongelamento(congelamento.id, { ...corpo, version: congelamento.version })
        : criarCongelamento(corpo);
    },
    onMutate: () => setFalha(null),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesDeMudanca.raiz });
      void cliente.invalidateQueries({ queryKey: ['chamados'] });
      aoFechar();
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const alternarNaLista = (
    valor: string,
    lista: string[],
    definir: (proxima: string[]) => void,
  ) => {
    definir(lista.includes(valor) ? lista.filter((item) => item !== valor) : [...lista, valor]);
  };

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={congelamento ? `Editar "${congelamento.name}"` : 'Declarar congelamento'}
      descricao="Período em que não se mexe no ambiente. Um congelamento novo pode alcançar mudança já agendada — e é melhor descobrir isso agora do que na véspera."
    >
      <form
        className="space-y-4 px-5 py-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (!incompleto) gravar.mutate();
        }}
      >
        <Campo rotulo="Nome" htmlFor="congelamento-nome">
          <input
            id="congelamento-nome"
            value={nome}
            onChange={(evento) => setNome(evento.target.value)}
            className={CLASSE_DE_CAMPO}
            placeholder="Fechamento contábil de dezembro"
          />
        </Campo>

        <Campo
          rotulo="Motivo"
          htmlFor="congelamento-motivo"
          dica="Vai aparecer na recusa do agendamento. Quem for barrado lê esta frase — vale escrevê-la para essa pessoa."
        >
          <textarea
            id="congelamento-motivo"
            rows={2}
            value={motivo}
            onChange={(evento) => setMotivo(evento.target.value)}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo rotulo="Começa em" htmlFor="congelamento-inicio">
            <input
              id="congelamento-inicio"
              type="datetime-local"
              value={inicio}
              onChange={(evento) => setInicio(evento.target.value)}
              className={CLASSE_DE_CAMPO}
            />
          </Campo>
          <Campo
            rotulo="Termina em"
            htmlFor="congelamento-fim"
            dica={
              intervaloValido || fim === '' ? undefined : 'O fim precisa vir depois do começo.'
            }
          >
            <input
              id="congelamento-fim"
              type="datetime-local"
              value={fim}
              onChange={(evento) => setFim(evento.target.value)}
              className={CLASSE_DE_CAMPO}
            />
          </Campo>
        </div>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Rigor</legend>
          <label className="flex items-start gap-2 rounded-md border border-border px-3 py-2 text-sm">
            <input
              type="radio"
              name="enforcement"
              checked={enforcement === 'BLOCKING'}
              onChange={() => setEnforcement('BLOCKING')}
              className="mt-1"
            />
            <span>
              <strong>Bloqueante</strong> — o agendamento é recusado com 422, e a única saída é uma
              exceção aprovada por autoridade superior.
            </span>
          </label>
          <label className="flex items-start gap-2 rounded-md border border-border px-3 py-2 text-sm">
            <input
              type="radio"
              name="enforcement"
              checked={enforcement === 'ADVISORY'}
              onChange={() => setEnforcement('ADVISORY')}
              className="mt-1"
            />
            <span>
              <strong>Informativo</strong> — aparece no calendário e na ficha da mudança, mas não
              impede nada.
            </span>
          </label>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Escopo</legend>
          <p className="text-xs text-muted-foreground">
            Nada marcado significa <strong>o tenant inteiro</strong> — é o padrão certo para
            congelamento de fim de ano. Com algo marcado, basta uma interseção: o congelamento
            alcança a mudança que encosta em qualquer um dos itens.
          </p>

          {catalogo.data && catalogo.data.length > 0 ? (
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border p-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Serviços
              </p>
              {catalogo.data.map((servico) => (
                <label key={servico.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={servicos.includes(servico.id)}
                    onChange={() => alternarNaLista(servico.id, servicos, setServicos)}
                  />
                  {servico.name}
                </label>
              ))}
            </div>
          ) : null}

          {opcoes.data && opcoes.data.organizations.length > 0 ? (
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border p-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Organizações
              </p>
              {opcoes.data.organizations.map((organizacao) => (
                <label key={organizacao.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={organizacoes.includes(organizacao.id)}
                    onChange={() => alternarNaLista(organizacao.id, organizacoes, setOrganizacoes)}
                  />
                  {organizacao.name}
                </label>
              ))}
            </div>
          ) : null}
        </fieldset>

        {falha ? <AvisoDeFalha falha={falha} /> : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar} disabled={gravar.isPending}>
            Cancelar
          </Button>
          <Button type="submit" loading={gravar.isPending} disabled={incompleto}>
            {congelamento ? 'Salvar' : 'Declarar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
